import { useEffect, useRef, useState } from 'react'
import { Button } from './ui'

/**
 * C-06 (REQ-042-002): campo dinamico "Firma". L'utente traccia la firma con dito o mouse; il valore e' un'immagine
 * PNG in data URL. Solo raccolta interna: nessun valore di firma elettronica.
 */
export function SignaturePad({
  id,
  label,
  error,
  value,
  disabled,
  onChange,
}: {
  id: string
  label: string
  error: string | undefined
  value: string
  disabled: boolean
  onChange: (v: string) => void
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const [dirty, setDirty] = useState(false)

  // Mostra la firma gia' salvata.
  useEffect(() => {
    const c = canvasRef.current
    const ctx = c?.getContext('2d')
    if (!c || !ctx) return
    ctx.clearRect(0, 0, c.width, c.height)
    if (!value.startsWith('data:image/png')) return
    const img = new Image()
    img.onload = () => ctx.drawImage(img, 0, 0, c.width, c.height)
    img.src = value
  }, [value])

  function pos(e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } {
    const c = canvasRef.current!
    const r = c.getBoundingClientRect()
    return { x: ((e.clientX - r.left) * c.width) / r.width, y: ((e.clientY - r.top) * c.height) / r.height }
  }

  function start(e: React.PointerEvent<HTMLCanvasElement>): void {
    if (disabled) return
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    drawing.current = true
    e.currentTarget.setPointerCapture(e.pointerId)
    const { x, y } = pos(e)
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.strokeStyle = '#111'
    ctx.beginPath()
    ctx.moveTo(x, y)
  }

  function move(e: React.PointerEvent<HTMLCanvasElement>): void {
    if (!drawing.current) return
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    const { x, y } = pos(e)
    ctx.lineTo(x, y)
    ctx.stroke()
    setDirty(true)
  }

  function end(): void {
    if (!drawing.current) return
    drawing.current = false
    const c = canvasRef.current
    if (c && dirty) onChange(c.toDataURL('image/png'))
  }

  function clear(): void {
    const c = canvasRef.current
    c?.getContext('2d')?.clearRect(0, 0, c.width, c.height)
    setDirty(false)
    onChange('')
  }

  return (
    <div>
      <label htmlFor={id}>{label}</label>
      <canvas
        id={id}
        ref={canvasRef}
        width={480}
        height={160}
        aria-label={label}
        style={{ width: '100%', maxWidth: 480, border: '1px solid currentColor', borderRadius: 8, touchAction: 'none', background: 'transparent' }}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
      />
      {!disabled && (
        <div>
          <Button variant="text" onClick={clear}>Cancella firma</Button>
        </div>
      )}
      {error && <div role="alert">{error}</div>}
    </div>
  )
}
